import { useEffect, useState, useRef, useCallback } from 'react';
import {
    Typography, Box, TextField, Button, Grid, Chip, IconButton,
    Select, MenuItem, FormControl, InputLabel, FormControlLabel, Switch,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    Alert, Snackbar, Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import SendIcon from '@mui/icons-material/Send';
import { getEmailTemplate, updateEmailTemplate, resetEmailTemplate, sendPreviewEmail } from '../../axios/axiosFunctions';
import EmailPreview from '../../components/EmailPreview';

const COLOR_PRESETS = ['#007bff', '#28a745', '#ff9800', '#dc3545', '#6f42c1', '#17a2b8', '#333333'];

const EmailTemplateEditor = ({ templateKey, onBack }) => {
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [resetDialogOpen, setResetDialogOpen] = useState(false);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [sendEmail, setSendEmail] = useState('');
    const [sending, setSending] = useState(false);
    const activeFieldRef = useRef(null);

    const fetchTemplate = useCallback(async () => {
        setLoading(true);
        const result = await getEmailTemplate(templateKey);
        if (result?.template) {
            setTemplate(result.template);
        }
        setLoading(false);
    }, [templateKey]);

    useEffect(() => {
        fetchTemplate();
    }, [fetchTemplate]);

    const handleFieldChange = (field, value) => {
        setTemplate(prev => ({ ...prev, [field]: value }));
    };

    const handleBodySectionChange = (index, field, value) => {
        setTemplate(prev => {
            const sections = [...(prev.bodySections || [])];
            sections[index] = { ...sections[index], [field]: value };
            return { ...prev, bodySections: sections };
        });
    };

    const addBodySection = () => {
        setTemplate(prev => ({
            ...prev,
            bodySections: [...(prev.bodySections || []), { text: '', style: 'body' }]
        }));
    };

    const removeBodySection = (index) => {
        setTemplate(prev => ({
            ...prev,
            bodySections: (prev.bodySections || []).filter((_, i) => i !== index)
        }));
    };

    const moveBodySection = (index, direction) => {
        setTemplate(prev => {
            const sections = [...(prev.bodySections || [])];
            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= sections.length) return prev;
            [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]];
            return { ...prev, bodySections: sections };
        });
    };

    const handlePostCtaChange = (index, field, value) => {
        setTemplate(prev => {
            const sections = [...(prev.postCtaSections || [])];
            sections[index] = { ...sections[index], [field]: value };
            return { ...prev, postCtaSections: sections };
        });
    };

    const addPostCtaSection = () => {
        setTemplate(prev => ({
            ...prev,
            postCtaSections: [...(prev.postCtaSections || []), { text: '', style: 'body' }]
        }));
    };

    const removePostCtaSection = (index) => {
        setTemplate(prev => ({
            ...prev,
            postCtaSections: (prev.postCtaSections || []).filter((_, i) => i !== index)
        }));
    };

    const insertVariable = (variable) => {
        const el = activeFieldRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const val = el.value;
        const insertion = `{{${variable}}}`;
        const newVal = val.substring(0, start) + insertion + val.substring(end);

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype, 'value'
        )?.set || Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
        )?.set;

        if (nativeInputValueSetter) {
            nativeInputValueSetter.call(el, newVal);
            el.dispatchEvent(new Event('input', { bubbles: true }));
        }

        setTimeout(() => {
            el.selectionStart = el.selectionEnd = start + insertion.length;
            el.focus();
        }, 0);
    };

    const handleSave = async () => {
        setSaving(true);
        const { _id, __v, ...data } = template;
        const result = await updateEmailTemplate(templateKey, data);
        setSaving(false);
        if (result?.error) {
            setSnackbar({ open: true, message: result.msg || 'Error saving', severity: 'error' });
        } else {
            setSnackbar({ open: true, message: 'Template saved successfully', severity: 'success' });
        }
    };

    const handleReset = async () => {
        setResetDialogOpen(false);
        setSaving(true);
        const result = await resetEmailTemplate(templateKey);
        setSaving(false);
        if (result?.template) {
            setTemplate(result.template);
            setSnackbar({ open: true, message: 'Template reset to default', severity: 'success' });
        } else {
            setSnackbar({ open: true, message: 'Error resetting template', severity: 'error' });
        }
    };

    const handleSendPreview = async () => {
        if (!sendEmail) return;
        setSending(true);
        const result = await sendPreviewEmail(templateKey, sendEmail);
        setSending(false);
        setSendDialogOpen(false);
        if (result?.error) {
            setSnackbar({ open: true, message: result.msg || 'Error sending preview', severity: 'error' });
        } else {
            setSnackbar({ open: true, message: `Preview sent to ${sendEmail}`, severity: 'success' });
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!template) {
        return (
            <Box>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack}>Back</Button>
                <Alert severity="error" sx={{ mt: 2 }}>Template not found</Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined">
                    Back
                </Button>
                <Typography variant="h4" fontWeight={500} color="secondary" sx={{ flex: 1 }}>
                    {template.displayName}
                </Typography>
                <Button
                    startIcon={<SendIcon />}
                    onClick={() => setSendDialogOpen(true)}
                    variant="outlined"
                    disabled={saving}
                >
                    Send Preview
                </Button>
                <Button
                    startIcon={<RestoreIcon />}
                    onClick={() => setResetDialogOpen(true)}
                    color="warning"
                    variant="outlined"
                    disabled={saving}
                >
                    Reset
                </Button>
                <Button
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    variant="contained"
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </Box>

            {/* Variable chips */}
            {template.availableVariables?.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Available Variables (click to insert at cursor)
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {template.availableVariables.map(v => (
                            <Chip
                                key={v}
                                label={`{{${v}}}`}
                                onClick={() => insertVariable(v)}
                                clickable
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        ))}
                    </Box>
                </Paper>
            )}

            <Grid container spacing={3}>
                {/* Left column - Editor */}
                <Grid size={{ xs: 12, md: 7 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Subject */}
                        <TextField
                            label="Email Subject"
                            value={template.subject || ''}
                            onChange={(e) => handleFieldChange('subject', e.target.value)}
                            fullWidth
                            onFocus={(e) => { activeFieldRef.current = e.target; }}
                        />

                        {/* Heading + Color */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                            <TextField
                                label="Heading"
                                value={template.heading || ''}
                                onChange={(e) => handleFieldChange('heading', e.target.value)}
                                sx={{ flex: 1 }}
                                onFocus={(e) => { activeFieldRef.current = e.target; }}
                            />
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {COLOR_PRESETS.slice(0, 4).map(c => (
                                    <Box
                                        key={c}
                                        onClick={() => handleFieldChange('headingColor', c)}
                                        sx={{
                                            width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                                            border: template.headingColor === c ? '3px solid #333' : '2px solid #ddd'
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>

                        {/* Sub-heading (if applicable) */}
                        {template.subHeading !== undefined && (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                                <TextField
                                    label="Sub-heading"
                                    value={template.subHeading || ''}
                                    onChange={(e) => handleFieldChange('subHeading', e.target.value)}
                                    sx={{ flex: 1 }}
                                    onFocus={(e) => { activeFieldRef.current = e.target; }}
                                />
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    {COLOR_PRESETS.map(c => (
                                        <Box
                                            key={c}
                                            onClick={() => handleFieldChange('subHeadingColor', c)}
                                            sx={{
                                                width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                                                border: template.subHeadingColor === c ? '3px solid #333' : '2px solid #ddd'
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Body Sections */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
                                Body Sections
                            </Typography>
                            {(template.bodySections || []).map((section, index) => (
                                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            <InputLabel>Style</InputLabel>
                                            <Select
                                                value={section.style || 'body'}
                                                label="Style"
                                                onChange={(e) => handleBodySectionChange(index, 'style', e.target.value)}
                                            >
                                                <MenuItem value="body">Body</MenuItem>
                                                <MenuItem value="instruction">Instruction</MenuItem>
                                                <MenuItem value="note">Note</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Box sx={{ flex: 1 }} />
                                        <IconButton size="small" onClick={() => moveBodySection(index, -1)} disabled={index === 0}>
                                            <ArrowUpwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => moveBodySection(index, 1)} disabled={index === (template.bodySections?.length || 0) - 1}>
                                            <ArrowDownwardIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => removeBodySection(index)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <TextField
                                        value={section.text || ''}
                                        onChange={(e) => handleBodySectionChange(index, 'text', e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Section text (HTML allowed)"
                                        onFocus={(e) => { activeFieldRef.current = e.target; }}
                                    />
                                </Paper>
                            ))}
                            <Button startIcon={<AddIcon />} onClick={addBodySection} size="small">
                                Add Section
                            </Button>
                        </Box>

                        {/* CTA Button */}
                        {template.templateKey !== 'otp' && template.templateKey !== 'setup_submission' && (
                            <Box>
                                <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
                                    Call-to-Action Button
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                                    <TextField
                                        label="Button Text"
                                        value={template.ctaButtonText || ''}
                                        onChange={(e) => handleFieldChange('ctaButtonText', e.target.value)}
                                        sx={{ flex: 1 }}
                                    />
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {COLOR_PRESETS.map(c => (
                                            <Box
                                                key={c}
                                                onClick={() => handleFieldChange('ctaButtonColor', c)}
                                                sx={{
                                                    width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                                                    border: template.ctaButtonColor === c ? '3px solid #333' : '2px solid #ddd'
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Conditional Deadline */}
                        {(template.templateKey === 'reviewer_launch' || template.templateKey === 'reminder') && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={template.hasConditionalDeadline || false}
                                            onChange={(e) => handleFieldChange('hasConditionalDeadline', e.target.checked)}
                                        />
                                    }
                                    label="Show deadline text when deadline is set"
                                />
                                {template.hasConditionalDeadline && (
                                    <TextField
                                        label="Deadline Text"
                                        value={template.deadlineText || ''}
                                        onChange={(e) => handleFieldChange('deadlineText', e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        sx={{ mt: 1 }}
                                        onFocus={(e) => { activeFieldRef.current = e.target; }}
                                    />
                                )}
                            </Box>
                        )}

                        {/* Post-CTA Sections */}
                        <Box>
                            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
                                Post-CTA Sections
                            </Typography>
                            {(template.postCtaSections || []).map((section, index) => (
                                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                                        <IconButton size="small" onClick={() => removePostCtaSection(index)} color="error">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <TextField
                                        value={section.text || ''}
                                        onChange={(e) => handlePostCtaChange(index, 'text', e.target.value)}
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Section text (HTML allowed)"
                                        onFocus={(e) => { activeFieldRef.current = e.target; }}
                                    />
                                </Paper>
                            ))}
                            <Button startIcon={<AddIcon />} onClick={addPostCtaSection} size="small">
                                Add Section
                            </Button>
                        </Box>

                        {/* Closing Text */}
                        <TextField
                            label="Closing Text"
                            value={template.closingText || ''}
                            onChange={(e) => handleFieldChange('closingText', e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            onFocus={(e) => { activeFieldRef.current = e.target; }}
                        />
                    </Box>
                </Grid>

                {/* Right column - Preview */}
                <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{ position: 'sticky', top: 20 }}>
                        <EmailPreview template={template} />
                    </Box>
                </Grid>
            </Grid>

            {/* Reset Confirmation Dialog */}
            <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
                <DialogTitle>Reset Template?</DialogTitle>
                <DialogContent>
                    <Typography>
                        This will revert all changes to the original default template. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleReset} color="warning" variant="contained">Reset</Button>
                </DialogActions>
            </Dialog>

            {/* Send Preview Dialog */}
            <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Send Preview Email</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Send a preview of this template with sample data to an email address.
                    </Typography>
                    <TextField
                        label="Recipient Email"
                        type="email"
                        value={sendEmail}
                        onChange={(e) => setSendEmail(e.target.value)}
                        fullWidth
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSendPreview(); }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSendDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleSendPreview}
                        variant="contained"
                        disabled={!sendEmail || sending}
                        startIcon={<SendIcon />}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EmailTemplateEditor;
